import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, RotateCw, Undo2, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { LastResults } from "../components/LastResults";
import { LuxeWheel, WheelSheen, PearlBall } from "../components/WheelLuxe";
import { playRouletteSpin, unlockAudio, playCoinSplash } from "../lib/rouletteSound";

// Mini Roulette — 19 pockets: a single green 0, then 1-18 alternating red (odd) /
// black (even). MUST match the server order in MINI_ROULETTE.
function buildPockets() {
  const out = [{ index: 0, label: "0", color: "green", number: 0 }];
  for (let num = 1; num <= 18; num++) {
    out.push({ index: num, label: String(num), color: num % 2 === 1 ? "red" : "black", number: num });
  }
  return out;
}
const POCKETS = buildPockets();
const N = POCKETS.length;         // 19
const SEG = 360 / N;
const FILL = { red: "#dc2626", black: "#18181b", green: "#22c55e" };
const COLOR_CLS = { red: "bg-red-600", black: "bg-zinc-900 border border-white/10", green: "bg-primary" };
const CX = 130, CY = 130, R = 122, LABEL_R = 100, BALL_R = 112;


const CHIPS = [20, 50, 100, 500, 1000, 2000, 5000, 10000];

const MINI_MAX_COVERAGE = 17;
// Distinct Mini pocket numbers (0-18) a single bet key covers.
const miniNumbersOf = (k) => {
  const [type, value] = k.split(":");
  if (type === "number") return [parseInt(value, 10)];
  if (type === "color") {
    if (value === "green") return [0];
    if (value === "red") return Array.from({ length: 18 }, (_, i) => i + 1).filter((n) => n % 2 === 1);
    if (value === "black") return Array.from({ length: 18 }, (_, i) => i + 1).filter((n) => n % 2 === 0);
  }
  return [];
};
const miniCoveredCount = (keys) => {
  const s = new Set();
  keys.forEach((k) => miniNumbersOf(k).forEach((n) => s.add(n)));
  return s.size;
};

export default function MiniRoulette() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [bets, setBets] = useState({});      // key "color:red" | "number:5" -> amount
  const [history, setHistory] = useState([]); // stack of { key, amt } for undo
  const [chip, setChip] = useState(100);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [wheelRot, setWheelRot] = useState(0);
  const ballTurns = useRef(0);
  const [ballRot, setBallRot] = useState(0);
  const [lastBet, setLastBet] = useState(null);
  const streakRef = useRef(0);
  const [recent, setRecent] = useState(() =>
    Array.from({ length: 50 }, () => {
      const p = POCKETS[Math.floor(Math.random() * POCKETS.length)];
      return { label: p.label, color: p.color };
    })
  );

  const rebet = () => {
    if (spinning || !lastBet) return;
    setBets({ ...lastBet });
    setHistory(Object.entries(lastBet).map(([key, amt]) => ({ key, amt })));
    setResult(null);
  };

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="mini-roulette-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="mini-roulette-go-home">Go Home</Button></Link>
    </div>
  );

  const total = Object.values(bets).reduce((a, b) => a + b, 0);

  const placeChip = (key) => {
    if (spinning) return;
    if (miniCoveredCount([...Object.keys(bets), key]) > MINI_MAX_COVERAGE) {
      toast({ title: "This type of betting is not allowed", description: `You can cover at most ${MINI_MAX_COVERAGE} pockets per spin on Mini Roulette.` });
      return;
    }
    setBets((prev) => ({ ...prev, [key]: (prev[key] || 0) + chip }));
    setHistory((h) => [...h, { key, amt: chip }]);
    setResult(null);
  };

  const undo = () => {
    if (spinning || history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setBets((prev) => {
      const next = { ...prev };
      const v = (next[last.key] || 0) - last.amt;
      if (v > 0) next[last.key] = v; else delete next[last.key];
      return next;
    });
  };

  const clearAll = () => {
    if (spinning) return;
    setBets({}); setHistory([]);
  };

  const spin = async () => {
    if (total <= 0) { toast({ title: "Place a bet first" }); return; }
    if (total < 20) { toast({ title: "Minimum bet is ₹20", description: `Total stake ${inr(total)}` }); return; }
    if (total > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (total > balance) { toast({ title: "Insufficient balance", description: `Total stake ${inr(total)}` }); return; }
    const payload = {
      bets: Object.entries(bets).map(([k, amount]) => {
        const [type, value] = k.split(":");
        return { type, value, amount };
      }),
    };
    setLastBet({ ...bets });
    unlockAudio();
    setSpinning(true); setResult(null);
    let res;
    try {
      res = await gamesApi.miniRoulette(payload);
    } catch (e) {
      setSpinning(false);
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
      return;
    }
    const win = res.result; // server-decided { index, label, color }
    playRouletteSpin(4600);
    setWheelRot((prev) => Math.ceil(prev / 360) * 360 + 360 * 4 + (360 - win.index * SEG));
    ballTurns.current += 7;
    setBallRot(-360 * ballTurns.current);

    setTimeout(async () => {
      await refresh();
      setResult({ win, payout: res.payout });
      setRecent((prev) => [{ label: win.label, color: win.color }, ...prev].slice(0, 50));
      streakRef.current = res.payout > 0 ? streakRef.current + 1 : 0;
      if (res.payout > 0) playCoinSplash();
      setReveal({ label: win.label, color: win.color, won: res.payout > 0, payout: res.payout, streak: streakRef.current });
      setSpinning(false);
      setBets({}); setHistory([]);   // fresh table for the next round
    }, 4600);
  };

  const chipLabel = (v) => { const n = Number(v); return n >= 1000 ? (Number.isInteger(n / 1000) ? n / 1000 : (n / 1000).toFixed(1)) + "K" : String(n); };
  const Chip = ({ amt }) => {
    const n = Number(amt);
    const special = n >= 10000;
    const size = special ? 40 : n >= 5000 ? 34 : 30;
    return (
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 grid place-items-center rounded-full font-extrabold text-black pointer-events-none"
        style={{
          height: size, width: size, fontSize: special ? 12 : 11,
          background: special ? "radial-gradient(circle at 50% 30%, #fff7cc, #f5b90b 55%, #b45309)" : "radial-gradient(circle at 50% 32%, #fde68a, #f59e0b)",
          border: special ? "2px solid #fff7cc" : "2px dashed rgba(0,0,0,0.6)",
          boxShadow: special ? "0 0 14px rgba(245,158,11,0.95), 0 2px 6px rgba(0,0,0,0.55)" : "0 2px 5px rgba(0,0,0,0.5)",
        }}>{chipLabel(amt)}</span>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-28" data-testid="mini-roulette-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Mini Roulette</h1>
          <p className="text-sm text-muted-foreground">19 pockets · multi-bet · colour 2x · number/green pays 1:15</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="mini-roulette-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Virtual wheel + ball */}
      <div className="relative rounded-2xl border border-border bg-gradient-to-b from-secondary/30 to-card p-6 flex flex-col items-center">
        <LastResults items={recent.slice(0, 10)} testId="mini-roulette-last-results" />
        <div className="relative" style={{ width: 280, height: 280 }}>
          <LuxeWheel viewBox="0 0 260 260" width={280} height={280} cx={CX} cy={CY} r={R}
            pockets={POCKETS.map((p) => ({ label: p.label, color: p.color }))} labelFontSize={11}
            testId="mini-roulette-wheel"
            style={{
              transform: `rotate(${wheelRot}deg)`,
              transition: spinning ? "transform 4.4s cubic-bezier(0.12, 0.62, 0.2, 1)" : "none",
              filter: "drop-shadow(0 0 18px rgba(212,175,55,0.45))",
            }} />
          <WheelSheen />

          <div className="absolute inset-0" data-testid="mini-roulette-ball"
            style={{
              transform: `rotate(${ballRot}deg)`,
              transition: spinning ? "transform 4.4s cubic-bezier(0.1, 0.6, 0.2, 1)" : "none",
            }}>
            <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ top: `${140 - BALL_R}px`, width: 16, height: 16 }}>
              <PearlBall size={18} />
            </div>
          </div>
        </div>

        <div className="mt-4 h-7 text-center" data-testid="mini-roulette-result">
          {result ? (
            <span className="font-semibold">
              Ball landed on <span className="font-display font-bold text-lg">{result.win.label}</span>{" "}
              <span className="capitalize" style={{ color: FILL[result.win.color] }}>{result.win.color}</span>
              {result.payout > 0 && <span className="text-primary"> · Won {inr(result.payout)}</span>}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{spinning ? "Ball is rolling…" : "Stack chips on any bets, then spin"}</span>
          )}
        </div>

        <div className="mt-3 flex items-center justify-center gap-4">
          <div className="text-sm"><span className="text-muted-foreground">Stake </span><span className="font-display font-bold text-lg" data-testid="mini-roulette-total-stake">{inr(total)}</span><span className="ml-1 text-xs text-muted-foreground">(min ₹20 · max ₹100,000)</span></div>
          <Button className="font-semibold h-12 px-10 text-base" onClick={spin} disabled={spinning} data-testid="mini-roulette-spin-btn">
            <RotateCw className={`h-4 w-4 mr-1.5 ${spinning ? "animate-spin" : ""}`} /> {spinning ? "Spinning…" : "Spin"}
          </Button>
        </div>
      </div>

      {/* Chip selector */}
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-1">Chip</span>
        {CHIPS.map((q) => {
          const special = q === 10000;
          const active = chip === q;
          return (
            <button key={q} onClick={() => setChip(q)} data-testid={`mini-roulette-chip-${q}`}
              className={`rounded-full font-bold border-2 transition-[color,background-color,border-color,opacity,transform] ${special ? "h-10 min-w-[64px] px-3.5 text-sm" : "h-9 min-w-[52px] px-3 text-sm"} ${
                active
                  ? "bg-yellow-400 text-black border-yellow-300 scale-105"
                  : special
                    ? "text-black border-yellow-200/70 shadow-md hover:brightness-110"
                    : "bg-secondary text-foreground border-transparent hover:brightness-125"
              }`}
              style={special && !active ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
              {chipLabel(q)}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={undo} disabled={spinning || history.length === 0} data-testid="mini-roulette-undo"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"><Undo2 className="h-3.5 w-3.5" /> Undo</button>
          <button onClick={clearAll} disabled={spinning || total === 0} data-testid="mini-roulette-clear"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-400 disabled:opacity-40"><X className="h-3.5 w-3.5" /> Clear</button>
        </div>
      </div>

      {/* Betting table (multi-bet: tap to stack the selected chip) */}
      <div className="mt-4 rounded-2xl border border-border bg-card p-6" data-testid="mini-roulette-table">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Colour · red/black pay 2x · green (0) pays 1:15</div>
        <div className="grid grid-cols-3 gap-2">
          {[["red", "Red 2x"], ["green", "Green & 0 16x"], ["black", "Black 2x"]].map(([c, lbl]) => {
            const key = `color:${c}`;
            return (
              <button key={c} data-testid={`mini-roulette-color-${c}`} onClick={() => placeChip(key)}
                className={`relative rounded-xl py-4 text-sm font-bold text-white transition-[color,background-color,border-color,opacity,transform] ${COLOR_CLS[c]} ${
                  bets[key] ? "ring-2 ring-yellow-400 scale-[1.02]" : "opacity-80 hover:opacity-100"
                }`}>{lbl}{bets[key] > 0 && <Chip amt={bets[key]} />}</button>
            );
          })}
        </div>

        <div className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Straight number · pays 1:15 (16x)</div>
        <div className="flex flex-wrap gap-1.5">
          {POCKETS.filter((p) => p.number >= 1).map((p) => {
            const key = `number:${p.number}`;
            return (
              <button key={p.number} data-testid={`mini-roulette-num-${p.number}`} onClick={() => placeChip(key)}
                className={`relative h-9 w-9 grid place-items-center rounded-md text-xs font-bold text-white transition-[color,background-color,border-color,opacity,transform] ${COLOR_CLS[p.color]} ${
                  bets[key] ? "ring-2 ring-yellow-400 scale-105" : "opacity-80 hover:opacity-100"
                }`}>{p.number}{bets[key] > 0 && <Chip amt={bets[key]} />}</button>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-muted-foreground text-center">Tap any colour or number to stack the selected chip. Bet on as many as you like.</p>
      </div>

      {lastBet && Object.keys(lastBet).length > 0 && (
        <div className="mt-4 flex justify-center">
          <button onClick={rebet} disabled={spinning} data-testid="mini-roulette-rebet-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary px-4 py-1.5 text-xs font-semibold hover:bg-primary/25 disabled:opacity-40 transition-colors">
            <RotateCw className="h-3.5 w-3.5" /> Rebet ₹{Object.values(lastBet).reduce((a, b) => a + b, 0)}
          </button>
        </div>
      )}
    </div>
  );
}
